import type { CatalogPage } from '../types/catalog-enrichment.types';
import type { CatalogPagination } from '../types/catalog-service.api.types';
import type { CurFeedProductWire } from '../types/curation.api.types';
import type { FeedProductRow } from '../types/curation.types';

/**
 * Reads `batch.run_id` off a listing item. The server sends it (the run the row was
 * last listed under) but the generated `CurBatch` schema omits it, so it is read
 * defensively rather than typed.
 */
function batchRunId(batch: CurFeedProductWire['batch']): string | null {
  if (!batch) return null;
  const runId = (batch as Record<string, unknown>).run_id;
  return typeof runId === 'string' ? runId : null;
}

/**
 * Reads `product.product_level` (UNIQUE / VARIANT). The serializer sends it beside the typed
 * keys but the generated `CurProduct` schema omits it, so it is read defensively.
 */
function productLevel(product: CurFeedProductWire['product']): string | null {
  if (!product) return null;
  const level = (product as Record<string, unknown>).product_level;
  return typeof level === 'string' ? level : null;
}

/** A non-empty string field off a loosely typed curation item (`null` for absent / empty / non-string). */
function optionalString(item: Record<string, unknown>, key: string): string | null {
  const value = item[key];
  return typeof value === 'string' && value ? value : null;
}

/**
 * `still_current` (§50, `source=auto`): false when a newer run has taken the row over.
 * Null when the backend does not send it (older deploys list only rows the run still holds).
 */
function stillCurrent(item: Record<string, unknown>): boolean | null {
  const value = item.still_current;
  return typeof value === 'boolean' ? value : null;
}

/** The first `optimized_image_assets` entry (§50 image record: url + 160px thumbnail), if sent. */
function firstOptimizedAsset(item: Record<string, unknown>): Record<string, unknown> | null {
  const assets = item.optimized_image_assets;
  if (!Array.isArray(assets)) return null;
  const first: unknown = assets[0];
  return typeof first === 'object' && first !== null ? (first as Record<string, unknown>) : null;
}

/**
 * The full-size image of a curation feed row — for enlarged / focus views: its first optimized
 * copy (`optimized_image_assets[0].url`, else the flat `optimized_images[0]`, a ~20–50 KB WebP),
 * else the merchant's source `image_url` — which can be a full-size JPEG off a slow storefront CDN.
 */
export function curationFullUrl(item: Record<string, unknown>): string | null {
  const asset = firstOptimizedAsset(item);
  if (asset) {
    const url = optionalString(asset, 'url');
    if (url) return url;
  }
  const optimized = item.optimized_images;
  if (Array.isArray(optimized)) {
    const first = optimized.find((url): url is string => typeof url === 'string' && url !== '');
    if (first) return first;
  }
  return optionalString(item, 'image_url');
}

/**
 * The small-thumb URL of a curation feed row: the 160px `thumbnail_url` (§50; also on the first
 * `optimized_image_assets` entry), else the full image (`curationFullUrl`) on older payloads or
 * images without a recorded thumbnail.
 */
export function curationThumbUrl(item: Record<string, unknown>): string | null {
  return (
    optionalString(item, 'thumbnail_url') ??
    optionalString(firstOptimizedAsset(item) ?? {}, 'thumbnail_url') ??
    curationFullUrl(item)
  );
}

/** `superseded_by` (§51): the later run that re-curated this listing, or null. */
function supersededBy(item: Record<string, unknown>): FeedProductRow['supersededBy'] {
  const value = item.superseded_by;
  if (!value || typeof value !== 'object') return null;
  const v = value as Record<string, unknown>;
  if (typeof v.run_id !== 'string' || typeof v.at !== 'string') return null;
  return {
    runId: v.run_id,
    runName: typeof v.run_name === 'string' && v.run_name ? v.run_name : null,
    at: v.at,
  };
}

/**
 * `ambiguous` (§50, `source=auto`): the run's ledger names the product, not this listing, and
 * this sibling stands for it. Null when not sent.
 */
function ambiguous(item: Record<string, unknown>): boolean | null {
  const value = item.ambiguous;
  return typeof value === 'boolean' ? value : null;
}

/**
 * Mid-task addendum — `flagged_by_run_id` + `flagged_by_run_name` (B1): which batch raised the
 * CURRENT flag. Null for a manual flag, an unflagged row, or a deploy without it; never guessed
 * from `runId` (this row's own run, a different thing).
 */
function flaggedByRun(item: Record<string, unknown>): FeedProductRow['flaggedByRun'] {
  const runId = item.flagged_by_run_id;
  if (typeof runId !== 'string' || !runId) return null;
  const runName = item.flagged_by_run_name;
  return { runId, runName: typeof runName === 'string' && runName ? runName : null };
}

/**
 * Normalises one snake_case curation listing item into a table row. The listing
 * schemas type items as `Record<string, unknown>`; every curation listing fills
 * them from the same serializer as the typed `CurFeedProduct`, so it is read as one.
 */
export function toFeedProductRow(item: Record<string, unknown>): FeedProductRow {
  const wire = item as CurFeedProductWire;
  // The top-level flag is the effective one (run row OR durable product flag).
  const needsAttention =
    wire.needs_attention ??
    wire.batch?.needs_attention ??
    wire.product_level?.needs_attention ??
    false;
  return {
    feedProductId: wire.feed_product_id,
    feedSource: wire.feed_source ?? null,
    sourceProductId: wire.source_product_id ?? null,
    title: wire.title ?? null,
    brand: wire.brand ?? null,
    imageUrl: wire.image_url ?? null,
    thumbUrl: curationThumbUrl(item),
    fullUrl: curationFullUrl(item),
    priceAmount: wire.price_amount ?? null,
    priceCurrency: wire.price_currency ?? null,
    salePriceAmount: wire.sale_price_amount ?? null,
    salePriceCurrency: wire.sale_price_currency ?? null,
    feedLabel: wire.feed_label ?? null,
    language: wire.language ?? null,
    availability: wire.availability ?? null,
    category: wire.google_product_category ?? null,
    state: wire.state ?? null,
    isNew: wire.is_new ?? null,
    needsAttention,
    labelCode: wire.needs_attention_label_code ?? null,
    labelDescription: wire.needs_attention_label_description ?? null,
    flaggedBy: wire.needs_attention_updated_by ?? null,
    // B1 — confirmed wire field names (`flagged_by_run_id`/`flagged_by_run_name`); read
    // defensively via `optionalString` (never a hard type dependency on the generated wire) so an
    // absent/renamed field still degrades to null rather than a crash.
    flaggedByRunId: optionalString(item, 'flagged_by_run_id'),
    flaggedByRunName: optionalString(item, 'flagged_by_run_name'),
    // Served as `needs_attention_since` (the column is `needs_attention_at`, which the screen used
    // to read and never matched); the product-level block carries it for a durable product flag.
    flaggedSince: wire.needs_attention_since ?? wire.product_level?.needs_attention_since ?? null,
    // Top-level since §50; older deploys carry it only on the run's `batch`.
    flagNotes: wire.needs_attention_notes ?? wire.batch?.notes ?? null,
    stillCurrent: stillCurrent(item),
    supersededBy: supersededBy(item),
    ambiguous: ambiguous(item),
    flaggedByRun: flaggedByRun(item),
    runId: batchRunId(wire.batch),
    productId: wire.product?.id ?? null,
    productStatus: wire.product?.status ?? null,
    productLevel: productLevel(wire.product),
    // Skipped listing only (additive, read defensively): why the run passed this row over.
    skipReason: optionalString(item, 'skip_reason') ?? optionalString(item, 'skipReason'),
    skipSource: skipSourceOf(optionalString(item, 'source')),
  };
}

/**
 * Builds a UI page from the nested `{ pagination, items }` block these curation
 * reads put inside `data` (unlike the flat list envelope `toPage` handles).
 */
export function toItemsPage(
  pagination: CatalogPagination,
  items: Record<string, unknown>[],
): CatalogPage<FeedProductRow> {
  return {
    items: items.map(toFeedProductRow),
    total: pagination.total,
    page: pagination.page,
    size: pagination.size,
    totalPages: pagination.totalPages,
    nextCursor: pagination.nextCursor ?? null,
  };
}

/** A skipped row's provenance (§87) — anything else (older deploys, other listings) is null. */
function skipSourceOf(value: string | null | undefined): 'RECORDED' | 'RECONSTRUCTED' | null {
  return value === 'RECORDED' || value === 'RECONSTRUCTED' ? value : null;
}
